import React, { useCallback, useRef, useState } from "react";
import { Box, Stack, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

const NAVY = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_PALE = "#FBF7EE";
const HAIRLINE = "rgba(10,14,26,0.06)";

export interface AttachmentDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** Inclusive cap on the number of files. Email Hub allows 10,
   *  Lead → Send Email allows 5 — caller passes whatever its backend
   *  validator enforces so the UI doesn't accept rejects. */
  maxFiles?: number;
  /** Per-file size cap in bytes. Same rationale as maxFiles. */
  maxBytesEach?: number;
  /** Short hint shown inside the drop area. */
  hint?: string;
}

const formatBytes = (b: number): string => {
  if (b < 1024)             return `${b} B`;
  if (b < 1024 * 1024)      return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Shared drag-and-drop attachment area for the send-email composer.
 * Renders:
 *   - a drop zone with drag-over highlight
 *   - a [browse] click target as a fallback
 *   - a chip per attached file with name + size + remove control
 *   - a footer line showing total size and per-file cap
 *
 * Upload progress (real bytes-on-the-wire) belongs to the parent
 * send call — this component is presentation + selection only.
 * When the backend supports per-file upload progress we'll surface
 * it here without changing the public API.
 */
const AttachmentDropzone: React.FC<AttachmentDropzoneProps> = ({
  files, onChange, maxFiles = 10, maxBytesEach = 25 * 1024 * 1024,
  hint = "Drag and drop files here, or click to browse",
}) => {
  const [dragging, setDragging] = useState(false);
  const [warning, setWarning]   = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptFiles = useCallback((incoming: File[]) => {
    setWarning(null);
    const accepted: File[] = [];
    const rejected: string[] = [];

    for (const f of incoming) {
      if (f.size > maxBytesEach) {
        rejected.push(`${f.name} (over ${formatBytes(maxBytesEach)})`);
        continue;
      }
      accepted.push(f);
    }

    let next = [...files, ...accepted];
    if (next.length > maxFiles) {
      const trimmed = next.length - maxFiles;
      next = next.slice(0, maxFiles);
      rejected.push(`${trimmed} extra file${trimmed === 1 ? "" : "s"} (max ${maxFiles})`);
    }
    onChange(next);
    if (rejected.length) setWarning(`Skipped: ${rejected.join(", ")}`);
  }, [files, maxFiles, maxBytesEach, onChange]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) acceptFiles(Array.from(e.dataTransfer.files));
  };

  const onBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) acceptFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const removeAt = (idx: number) => onChange(files.filter((_, i) => i !== idx));

  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

  return (
    // Root container deliberately swallows every click / mousedown
    // that originates inside the dropzone subtree. This component is
    // mounted in two distinct contexts:
    //
    //   • Inside ComposeModal (a portal-rendered MUI Dialog) — without
    //     this guard, a click on a file chip or the browse area bubbles
    //     through React's synthetic event tree to whatever container
    //     mounted the modal (most painfully: <TableRow onClick={open
    //     thread}> on the Inquiries page), opening the lead drawer over
    //     the modal and making it look like the modal "closed".
    //   • Inside QuickActionsCard's inline email panel — same risk if
    //     that panel ever lives inside a clickable ancestor.
    //
    // Stop propagation here once, everywhere this component renders.
    // Inner handlers (file-picker click, drag/drop, chip delete) still
    // run first; we only block the bubble *past* this root.
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* ── Drop zone ─────────────────────────────────────────── */}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          cursor: "pointer",
          p: 1.5, borderRadius: "10px",
          bgcolor: dragging ? CHAMPAGNE_PALE : "#fafbfc",
          border: `1.5px dashed ${dragging ? CHAMPAGNE : "#cbd5e1"}`,
          transition: "all 150ms",
          textAlign: "center",
          "&:hover": { bgcolor: CHAMPAGNE_PALE, borderColor: CHAMPAGNE },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
          <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: dragging ? CHAMPAGNE : "#64748b" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: dragging ? "#A8893E" : NAVY }}>
            {hint}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.4 }}>
          Up to {maxFiles} files · {formatBytes(maxBytesEach)} each
        </Typography>
        <input
          ref={inputRef} type="file" multiple hidden onChange={onBrowse}
        />
      </Box>

      {/* ── Attached file chips ───────────────────────────────── */}
      {files.length > 0 && (
        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap mt={1}>
          {files.map((f, i) => (
            <Chip
              key={`${f.name}-${i}`}
              size="small"
              icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 14 }} />}
              label={
                <Stack direction="row" alignItems="center" gap={0.4}>
                  <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 600, color: NAVY }} noWrap>
                    {f.name}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 9.5, color: "#94a3b8" }}>
                    {formatBytes(f.size)}
                  </Typography>
                </Stack>
              }
              // Wrap removeAt in a stop-propagation closure so the
              // delete icon click doesn't bubble out of the dropzone
              // either (defense-in-depth alongside the root onClick
              // guard above).
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              onDelete={(e: React.MouseEvent) => { e.stopPropagation(); removeAt(i); }}
              deleteIcon={<CloseIcon sx={{ fontSize: "12px !important" }} />}
              sx={{
                maxWidth: 220, height: 24,
                bgcolor: "#fff", border: `1px solid ${HAIRLINE}`,
                "& .MuiChip-deleteIcon:hover": { color: "#dc2626" },
              }}
            />
          ))}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Add more files" arrow>
            <IconButton
              size="small"
              onClick={() => inputRef.current?.click()}
              sx={{
                border: `1px dashed ${HAIRLINE}`, borderRadius: 1.5,
                width: 24, height: 24, color: "#64748b",
                "&:hover": { color: NAVY, borderColor: CHAMPAGNE },
              }}
            >
              <AttachFileIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {(warning || files.length > 0) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.6}>
          {warning ? (
            <Typography sx={{ fontSize: 10.5, color: "#dc2626", fontWeight: 600 }}>
              {warning}
            </Typography>
          ) : <Box />}
          {files.length > 0 && (
            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
              {files.length}/{maxFiles} files · {formatBytes(totalBytes)} total
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default AttachmentDropzone;
